import type { Hermite } from '@fullstory/recording-api';
import type { FramePath } from '@fullstory/renderer-shared';
import type { Point } from '@fullstory/util/geometry';

/**
 * A point defined in the coordinate space of a specific frame.
 */
export type FramePoint = {
  readonly frame: FramePath;
  readonly point: Readonly<Point>;
};

/**
 * A Hermite curve, as a parametric function suitable for use with
 * {@link PiecewiseFunction}.
 */
export class HermiteCurve {
  constructor(private _curve: Hermite) {}

  at(a: number): number {
    return evaluateHermiteCurve(a, this._curve);
  }
}

/**
 * A 2D curve expressed as an independent Hermite curve for each coordinate,
 * implemented as a parametric function suitable for use with {@link PiecewiseFunction}.
 */
export class HermiteCurve2d {
  constructor(private _xCurve: Hermite, private _yCurve: Hermite) {}

  at(a: number): Point {
    return {
      x: evaluateHermiteCurve(a, this._xCurve),
      y: evaluateHermiteCurve(a, this._yCurve),
    };
  }
}

/**
 * A 2D curve expressed as an independent Hermite curve for each coordinate,
 * specified in the coordinate system of a specific frame, and implemented as a
 * parametric function suitable for use with {@link PiecewiseFunction}.
 */
export class FrameHermiteCurve2d {
  constructor(private _xCurve: Hermite, private _yCurve: Hermite, private _frame: FramePath) {}

  at(a: number): FramePoint {
    return {
      frame: this._frame,
      point: {
        x: evaluateHermiteCurve(a, this._xCurve),
        y: evaluateHermiteCurve(a, this._yCurve),
      },
    };
  }
}

/**
 * @param alpha - A position along a curve.
 * @param curve - A description of the curve in terms of Hermite parameters.
 * @return the value of the curve at position 'alpha'.
 */
export function evaluateHermiteCurve(alpha: number, curve: Hermite): number {
  // Clamp the input so that we're guaranteed to lie on the curve.
  const a = Math.max(Math.min(alpha, 1), 0);

  // Hermite spline basis functions.
  const a3 = a * a * a,
    a2 = a * a;
  const h1 = 2 * a3 + -3 * a2 + 1;
  const h2 = -2 * a3 + 3 * a2;
  const h3 = a3 + -2 * a2 + a;
  const h4 = a3 + -1 * a2;

  return h1 * curve[0] + h2 * curve[1] + h3 * curve[2] + h4 * curve[3];
}
